/**
 * NLP Service
 * Translates natural language queries to GIS query parameters using OpenAI
 * Uses backend proxy to keep API keys secure
 */

import { featureServices, isWaterTenant, isEmissionsTenant } from '../config/gisConfig';
import conversationService from './conversationService';

/**
 * Water system prompt — Select Water Solutions
 */
const WATER_SYSTEM_PROMPT = `You are a GIS query assistant for Select Water Solutions. Convert natural language queries into structured GIS query parameters for water asset management.

COMPANY CONTEXT:
Select Water Solutions manages water sourcing, disposal, treatment, and transfer infrastructure across the Permian Basin.

AVAILABLE LAYERS AND FIELD NAMES (case-sensitive):

1. "sources" - Water Sources:
   - id (string), name (string), county (string), basin (string)
   - type (string) - source type
   - daily_volume_bbl (number) - daily volume in barrels
   - operator (string), status (string), quality_tds (number)

2. "disposal" - Disposal Wells:
   - id (string), name (string), county (string), basin (string)
   - type (string), capacity_bbl (number), daily_volume_bbl (number)
   - utilization_pct (number), operator (string), status (string)
   - formation (string), permit (string)

3. "pipelines" - Transfer Pipelines:
   - id (string), name (string), county (string), type (string)
   - length_miles (number), capacity_bbl_day (number), utilization_pct (number)
   - operator (string), status (string), product (string)

4. "treatment" - Treatment Facilities:
   - id (string), name (string), county (string), basin (string)
   - type (string), capacity_bbl (number), daily_volume_bbl (number)
   - recycling_rate_pct (number), operator (string), status (string)
   - technology (string), output_quality (string)

RESPONSE FORMAT (JSON):
{
  "layer": "sources|disposal|pipelines|treatment",
  "conditions": [{"field": "county", "operator": "=", "value": "MIDLAND", "type": "string"}],
  "zoom": false,
  "description": "Showing disposal wells in Midland",
  "confidence": 0.9
}

Important: Always return valid JSON. Match county names in uppercase.`;

/**
 * Emissions system prompt: Picarro gas utility leak detection and fenceline monitoring
 */
const EMISSIONS_SYSTEM_PROMPT = `You are a GIS query assistant for Nexus Emissions, a gas utility leak detection and fenceline monitoring platform. Convert natural language queries into structured GIS query parameters.

DOMAIN CONTEXT:
The tenant operates gas distribution and transmission infrastructure with Picarro mobile leak surveys, fenceline methane sensors, and field repair work orders. Spatial focus is district-level utility service areas, not Texas counties.

AVAILABLE LAYERS AND FIELD NAMES (case-sensitive):

1. "leaks" - Leak Indications (Picarro mobile survey hits):
   - leak_id (string), severity (string: 'Grade 1'|'Grade 2'|'Grade 3'), status (string)
   - methane_ppm (number), district (string), detected_date (string)

2. "transmission_mains" - Transmission Mains:
   - pipe_id (string), material (string), diameter_in (number)
   - install_year (number), district (string)

3. "distribution_mains" - Distribution Mains:
   - pipe_id (string), material (string), diameter_in (number)
   - install_year (number), district (string)

4. "service_lines" - Service Lines:
   - line_id (string), material (string)

5. "survey_routes" - Survey Routes:
   - route_id (string), crew (string), status (string)

6. "pipe_risk" - Pipe Risk Scores:
   - pipe_id (string), risk_score (number 0-100)

7. "sensitive_premises" - Sensitive Premises (schools, hospitals):
   - premise_id (string), type (string)

8. "work_orders_open" - Open Work Orders:
   - wo_id (string), priority (string), status (string)

9. "work_orders_closed" - Completed Repairs:
   - wo_id (string)

10. "districts" - Operating Districts:
    - district_id (string), name (string)

11. "crews" - Field Crews:
    - crew_id (string), name (string), status (string)

12. "facility" - Facility Boundary (fenceline POC):
    - facility_id (string), name (string)

13. "process_units" - Process Units inside facility:
    - unit_id (string), type (string)

14. "sensors" - Fenceline Sensors:
    - sensor_id (string), type (string), status (string)

15. "dispersion_plumes" - Modeled Dispersion Plumes:
    - plume_id (string), concentration (number)

RESPONSE FORMAT (JSON):
{
  "layer": "leaks|distribution_mains|transmission_mains|service_lines|survey_routes|pipe_risk|sensitive_premises|work_orders_open|work_orders_closed|districts|crews|facility|process_units|sensors|dispersion_plumes",
  "conditions": [{"field": "severity", "operator": "=", "value": "Grade 1", "type": "string"}],
  "zoom": false,
  "description": "Showing Grade 1 leaks",
  "confidence": 0.9
}

Example queries:
- "Show all leaks" -> layer: leaks, no conditions
- "Show Grade 1 leaks" -> layer: leaks, conditions: severity = "Grade 1"
- "Show distribution mains" -> layer: distribution_mains, no conditions
- "Show fenceline sensors" -> layer: sensors, no conditions
- "Show high-risk pipes" -> layer: pipe_risk, conditions: risk_score >= 70
- "Show open work orders" -> layer: work_orders_open, no conditions

Important: Always return valid JSON. The emissions tenant uses district names, not Texas counties.`;


/**
 * System prompt for the LLM — TPL
 * This defines the GIS query translation task
 */
const SYSTEM_PROMPT = `You are a GIS query assistant for Texas Pacific Land Corporation (TPL). Convert natural language queries into structured GIS query parameters for TPL's land and royalty management system.

COMPANY CONTEXT:
TPL owns 880,000+ surface acres and 207,000 net royalty acres in West Texas Permian Basin across 20+ counties. TPL is NOT an oil/gas producer - they are LANDOWNERS who earn royalties from operators. Key business: land tract management, water services, pipeline easements, and surface lease agreements.

AVAILABLE LAYERS AND FIELD NAMES (case-sensitive):

1. "tracts" - TPL Land Tracts (TP_Assets_All):
   - TractID (string) - Unique tract identifier
   - TractType (string) - F=Fee, E=Easement, L=Lease, S=Surface, R=Royalty
   - CountyName (string) - County name
   - Block (string) - Block number
   - Sec (string) - Section number
   - TractAcres (number) - Acreage
   - Status (string) - Active, Expired, Pending
   - ORRI (number) - Override royalty interest (decimal: 0.025 = 2.5%)
   - NRI (number) - Net revenue interest (decimal: 0.02 = 2%)
   - NMA (number) - Net mineral acres
   - NRA (number) - Net royalty acres
   - DealName (string) - Deal/acquisition name
   - Grantee (string) - Grantee name
   - ContractID (string) - Contract identifier

2. "wells" - TPL Royalty Wells (TP_Royalty_Wells):
   - API (string) - API number (10-digit)
   - API14 (string) - Full 14-digit API
   - WellName (string) - Well name
   - WellPadID (string) - Well pad identifier
   - ENVOperator (string) - Operator name (e.g., "Diamondback Energy", "EOG Resources")
   - ENVWellStatus (string) - Well status: PRODUCING, DUC, DRILLING, PERMITTED, SHUT-IN, P&A
   - ENVInterval (string) - Formation: WOLFCAMP A/B/D, BONE SPRING, AVALON, DELAWARE, SPRABERRY
   - County (string) - County name
   - LateralLength (number) - Lateral length in feet
   - ActiveNRI (number) - Active net revenue interest
   - AllocatedNRI (number) - Allocated NRI
   - DSUName (string) - Development spacing unit name
   - DealName (string) - Deal name
   - Trajectory (string) - Well trajectory (HORIZONTAL, VERTICAL)
   - Vintage (string) - Year vintage

3. "blocks" - Landgrid Blocks:
   - SURVEYBLO3 (string) - Block number
   - CountyName (string) - County name
   - SURVEYNAME (string) - Survey name

4. "sections" - Survey Sections:
   - BlkSec (string) - Block-Section identifier
   - County (string) - County name
   - SURVEYSEC4 (string) - Section number

5. "contracts" - TPL Contracts (Easements/Leases):
   - TractID (string) - Tract identifier
   - TractType (string) - Contract type
   - ContractType (string) - Pipeline, Water, Surface, etc.
   - ContractCat (string) - Contract category
   - Status (string) - Active, Expired
   - Grantee (string) - Grantee/operator
   - Rate (number) - Rate value
   - EffectiveDate (string) - Effective date
   - Term (string) - Contract term

6. "dsus" - Development Spacing Units

7. "activeRigs" - Active Drilling Rigs

COMMON OPERATORS (use LIKE for partial matching):
Diamondback, Exxon, ExxonMobil, Occidental, Oxy, Ovintiv, APA Corp, Apache, EOG, Mewbourne, Permian Resources, ConocoPhillips, Devon

COUNTIES (SE New Mexico / West Texas):
Texas: REEVES, CULBERSON, LOVING, MARTIN, MIDLAND, ECTOR, WARD, WINKLER, ANDREWS, PECOS, CRANE, HOWARD, GLASSCOCK, REAGAN
New Mexico: LEA, EDDY, CHAVES

FORMATIONS/INTERVALS:
WOLFCAMP, WOLFCAMP A, WOLFCAMP B, WOLFCAMP D, BONE SPRING, AVALON, DELAWARE, SPRABERRY

WELL STATUS VALUES:
PRODUCING, DUC (Drilled Uncompleted), DRILLING, PERMITTED, SHUT-IN, P&A (Plugged & Abandoned)

SQL Operators:
- Use "=" for exact matches
- Use "LIKE" for partial matches (with % wildcards)
- Use "IN" for multiple values
- Use ">", "<", ">=", "<=" for numeric/date comparisons
- Use "BETWEEN" for ranges

Date Format:
- Use ISO format: YYYY-MM-DD
- For date queries: "PermitApprovedDate >= DATE '2025-08-01'"
- For date ranges: "PermitApprovedDate BETWEEN DATE '2025-08-01' AND DATE '2025-08-31'"

Return JSON with this exact structure:
{
  "layer": "tracts" | "contracts" | "wells",
  "conditions": [
    {
      "field": "FieldName",
      "operator": "=" | ">" | "<" | ">=" | "<=" | "LIKE" | "IN" | "BETWEEN",
      "value": "value or array of values",
      "type": "string" | "number" | "date"
    }
  ],
  "zoom": true | false,
  "description": "Human-readable explanation of what this query does",
  "confidence": 0.0 to 1.0 (how confident you are in this translation)
}

Examples:

User: "Show me tract F-1234"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "TractID", "operator": "LIKE", "value": "%F-1234%", "type": "string"}],
  "zoom": true,
  "description": "Displaying tract F-1234 details",
  "confidence": 0.95
}

User: "Find all tracts in Howard County"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "CountyName", "operator": "=", "value": "HOWARD", "type": "string"}],
  "zoom": true,
  "description": "Showing all TPL tracts in Howard County",
  "confidence": 0.95
}

User: "Show tracts with acreage over 500"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "TractAcres", "operator": ">", "value": 500, "type": "number"}],
  "zoom": false,
  "description": "Filtering tracts with acreage over 500",
  "confidence": 0.9
}

User: "Find tracts with NRI over 0.01"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "NRI", "operator": ">", "value": 0.01, "type": "number"}],
  "zoom": false,
  "description": "Showing tracts with NRI over 0.01 (1%)",
  "confidence": 0.9
}

User: "Show tracts with NRA over 20"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "NRA", "operator": ">", "value": 20, "type": "number"}],
  "zoom": false,
  "description": "Showing tracts with net royalty acres over 20",
  "confidence": 0.9
}

User: "Find tracts with ORRI over 2%"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "ORRI", "operator": ">", "value": 0.02, "type": "number"}],
  "zoom": false,
  "description": "Showing tracts with override royalty interest over 2% (0.02 decimal)",
  "confidence": 0.9
}

User: "Show fee tracts in Reeves County"
Response:
{
  "layer": "tracts",
  "conditions": [
    {"field": "TractType", "operator": "=", "value": "F", "type": "string"},
    {"field": "CountyName", "operator": "=", "value": "Reeves", "type": "string"}
  ],
  "zoom": true,
  "description": "Showing fee tracts (TractType=F) in Reeves County",
  "confidence": 0.95
}

User: "Show wells operated by Diamondback"
Response:
{
  "layer": "wells",
  "conditions": [{"field": "ENVOperator", "operator": "LIKE", "value": "%Diamondback%", "type": "string"}],
  "zoom": false,
  "description": "Displaying wells operated by Diamondback",
  "confidence": 0.9
}

User: "Show producing wells in Reeves County"
Response:
{
  "layer": "wells",
  "conditions": [
    {"field": "County", "operator": "=", "value": "REEVES", "type": "string"},
    {"field": "ENVWellStatus", "operator": "=", "value": "PRODUCING", "type": "string"}
  ],
  "zoom": true,
  "description": "Displaying producing wells in Reeves County",
  "confidence": 0.95
}

User: "Find Wolfcamp wells with lateral over 10000 ft"
Response:
{
  "layer": "wells",
  "conditions": [
    {"field": "ENVInterval", "operator": "LIKE", "value": "%WOLFCAMP%", "type": "string"},
    {"field": "LateralLength", "operator": ">", "value": 10000, "type": "number"}
  ],
  "zoom": false,
  "description": "Showing Wolfcamp wells with lateral length over 10,000 feet",
  "confidence": 0.9
}

User: "Show tracts in Block 32"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "Block", "operator": "LIKE", "value": "%32%", "type": "string"}],
  "zoom": true,
  "description": "Showing tracts in Block 32",
  "confidence": 0.9
}

User: "Find T&P RR CO survey tracts"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "Survey", "operator": "=", "value": "T&P RR CO", "type": "string"}],
  "zoom": false,
  "description": "Showing tracts in T&P RR CO survey",
  "confidence": 0.9
}

User: "Show tracts in Section 10"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "Sec", "operator": "=", "value": "10", "type": "string"}],
  "zoom": true,
  "description": "Showing tracts in Section 10",
  "confidence": 0.9
}

User: "Show SM ORRI Deal 1 tracts"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "DealName", "operator": "=", "value": "SM ORRI Deal 1", "type": "string"}],
  "zoom": true,
  "description": "Showing tracts from SM ORRI Deal 1",
  "confidence": 0.95
}

User: "Find Tumbleweed Deal assets"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "DealName", "operator": "LIKE", "value": "%Tumbleweed%", "type": "string"}],
  "zoom": true,
  "description": "Showing tracts from Tumbleweed Deal",
  "confidence": 0.9
}

User: "Show me just the Diamondback ones"
Response: (This is a FOLLOW-UP query - use previous context)
{
  "layer": "wells",
  "conditions": [
    {"field": "ENVOperator", "operator": "LIKE", "value": "%Diamondback%", "type": "string"}
  ],
  "zoom": false,
  "description": "Filtering previous results to Diamondback only",
  "confidence": 0.85,
  "isFollowUp": true
}

User: "What's the total acreage?"
Response: (ANALYTICS query - return special format)
{
  "layer": "analytics",
  "analysisType": "aggregate",
  "function": "sum",
  "field": "TractAcres",
  "description": "Calculating total acreage from previous results",
  "confidence": 0.95
}

User: "How many tracts do we have in Reeves County?"
Response: (ANALYTICS query)
{
  "layer": "analytics",
  "analysisType": "count",
  "baseQuery": {
    "layer": "tracts",
    "conditions": [{"field": "CountyName", "operator": "=", "value": "REEVES", "type": "string"}]
  },
  "description": "Counting tracts in Reeves County",
  "confidence": 0.95
}

User: "Summarize the wells by operator"
Response: (ANALYTICS query)
{
  "layer": "analytics",
  "analysisType": "groupBy",
  "field": "ENVOperator",
  "description": "Grouping wells by operator with counts",
  "confidence": 0.95
}

User: "Show all tracts within 5 miles of tract F-1234"
Response: (PROXIMITY query)
{
  "layer": "proximity",
  "analysisType": "buffer",
  "centerTract": "F-1234",
  "radius": 5,
  "unit": "miles",
  "description": "Finding all tracts within 5 miles of tract F-1234",
  "confidence": 0.95
}

User: "Find wells near DSU ABC-123 within 2 miles"
Response: (PROXIMITY query)
{
  "layer": "proximity",
  "analysisType": "buffer",
  "centerDSU": "ABC-123",
  "radius": 2,
  "unit": "miles",
  "description": "Finding wells within 2 miles of DSU ABC-123",
  "confidence": 0.9
}

User: "What tracts are close to well 42-109-32930?"
Response: (PROXIMITY query - default to 5 miles if not specified)
{
  "layer": "proximity",
  "analysisType": "buffer",
  "centerWell": "42-109-32930",
  "radius": 5,
  "unit": "miles",
  "description": "Finding tracts near well 42-109-32930 (within 5 miles)",
  "confidence": 0.85
}

User: "Show tracts within 5 miles of F-1234 with ORRI over 2%"
Response: (COMBINED PROXIMITY + ATTRIBUTE query)
{
  "layer": "proximity",
  "analysisType": "buffer",
  "centerTract": "F-1234",
  "radius": 5,
  "unit": "miles",
  "conditions": [
    {"field": "ORRI", "operator": ">", "value": 2, "type": "number"}
  ],
  "description": "Finding tracts within 5 miles of F-1234 with ORRI over 2%",
  "confidence": 0.9
}

User: "Find wells near tract F-5678 with status PRODUCING"
Response: (COMBINED PROXIMITY + ATTRIBUTE query)
{
  "layer": "proximity",
  "analysisType": "buffer",
  "centerTract": "F-5678",
  "radius": 5,
  "unit": "miles",
  "conditions": [
    {"field": "ENVWellStatus", "operator": "=", "value": "PRODUCING", "type": "string"}
  ],
  "description": "Finding producing wells near tract F-5678",
  "confidence": 0.9
}

User: "Show pipeline easements"
Response:
{
  "layer": "contracts",
  "conditions": [{"field": "ContractType", "operator": "LIKE", "value": "%Pipeline%", "type": "string"}],
  "zoom": false,
  "description": "Showing pipeline easement contracts",
  "confidence": 0.95
}

User: "Find active contracts with rate over 1000"
Response:
{
  "layer": "contracts",
  "conditions": [
    {"field": "Status", "operator": "=", "value": "Active", "type": "string"},
    {"field": "Rate", "operator": ">", "value": 1000, "type": "number"}
  ],
  "zoom": false,
  "description": "Finding active contracts with rate over $1,000",
  "confidence": 0.9
}

User: "Show surface leases granted to Exxon"
Response:
{
  "layer": "contracts",
  "conditions": [
    {"field": "ContractType", "operator": "LIKE", "value": "%Surface%", "type": "string"},
    {"field": "Grantee", "operator": "LIKE", "value": "%Exxon%", "type": "string"}
  ],
  "zoom": false,
  "description": "Showing surface lease contracts granted to Exxon",
  "confidence": 0.9
}

User: "Find water rights contracts"
Response:
{
  "layer": "contracts",
  "conditions": [{"field": "ContractType", "operator": "LIKE", "value": "%Water%", "type": "string"}],
  "zoom": false,
  "description": "Showing water rights contracts",
  "confidence": 0.95
}

Important:
- Always return valid JSON
- Use exact field names (case-sensitive)
- For OR conditions, use IN operator with array of values
- For follow-up queries (containing "just", "only", "those", "these"), set isFollowUp=true
- For analytics queries (containing "how many", "total", "average", "summarize"), use layer="analytics"
- For proximity/spatial queries (containing "within", "near", "close to", "around"), use layer="proximity" with analysisType="buffer"
- For contract-based queries (containing "monthly revenue", "contracts", "expiring", "pipeline easement", "water rights"), use layer="contracts" with contractFilters object
- For proximity queries, extract centerTract, radius (default 5 if not specified), and unit (miles or kilometers)
- For single ID lookups or specific location queries, set zoom=true
- For general filters, set zoom=false
- Set confidence lower if the query is ambiguous
- Match operator names flexibly (e.g., "bpx" matches "bpx energy")

MULTI-LAYER QUERIES (WOW FEATURE):
When user asks to see multiple data types together (tracts AND wells, or "show everything", "show all data", "show all layers"), return a multi-layer query:

User: "Show all data in Reeves County"
Response:
{
  "layer": "multi",
  "layers": ["tracts", "wells"],
  "conditions": [{"field": "County", "operator": "=", "value": "REEVES", "type": "string"}],
  "zoom": true,
  "description": "Showing all tracts and wells in Reeves County",
  "confidence": 0.95
}

User: "Show tracts and wells operated by Diamondback"
Response:
{
  "layer": "multi",
  "layers": ["tracts", "wells"],
  "conditions": [{"field": "ENVOperator", "operator": "LIKE", "value": "%Diamondback%", "type": "string"}],
  "zoom": true,
  "description": "Showing tracts and wells operated by Diamondback",
  "confidence": 0.9
}

User: "Show everything in Howard County"
Response:
{
  "layer": "multi",
  "layers": ["tracts", "wells", "contracts"],
  "conditions": [{"field": "County", "operator": "=", "value": "HOWARD", "type": "string"}],
  "zoom": true,
  "description": "Showing all data (tracts, wells, contracts) in Howard County",
  "confidence": 0.95
}

User: "Display all layers for Culberson"
Response:
{
  "layer": "multi",
  "layers": ["tracts", "wells"],
  "conditions": [{"field": "CountyName", "operator": "=", "value": "CULBERSON", "type": "string"}],
  "zoom": true,
  "description": "Displaying all layers for Culberson County",
  "confidence": 0.95
}

Note: For multi-layer queries, use County field for wells and CountyName for tracts. The backend will handle field mapping.`;

/**
 * Translate natural language query to GIS parameters
 * Uses backend proxy to keep OpenAI API key secure
 */
export async function translateQuery(userQuery, previousResults = null) {
  try {
    // Add user message to conversation history
    conversationService.addMessage('user', userQuery);

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

    // Build messages with conversation context
    const activePrompt = isEmissionsTenant
      ? EMISSIONS_SYSTEM_PROMPT
      : isWaterTenant
        ? WATER_SYSTEM_PROMPT
        : SYSTEM_PROMPT;
    const messages = [
      { role: 'system', content: activePrompt }
    ];

    // Add conversation history for context (last 3 exchanges)
    const context = conversationService.getContext();
    if (context.history.length > 0) {
      const recentHistory = context.history.slice(-6); // Last 3 exchanges (user + assistant)
      recentHistory.forEach(msg => {
        messages.push({
          role: msg.role,
          content: msg.content
        });
      });
    }

    // Add context about last query results if available
    if (context.lastResults) {
      messages.push({
        role: 'system',
        content: `PREVIOUS QUERY CONTEXT: The user's last query returned ${context.lastResults.count} ${context.lastResults.layer} records. Summary: ${JSON.stringify(context.lastResults.summary)}`
      });
    }

    // Add current user query
    messages.push({
      role: 'user',
      content: userQuery
    });

    const response = await fetch(`${backendUrl}/api/openai/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: 'gpt-4-turbo-preview',
        messages: messages,
        response_format: { type: 'json_object' },
        temperature: 0.3,
        max_tokens: 500
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'OpenAI request failed');
    }

    const completion = await response.json();
    const intent = JSON.parse(completion.choices[0].message.content);

    // Handle analytics queries
    if (intent.layer === 'analytics') {
      return intent;
    }

    // Handle multi-layer queries (WOW FEATURE)
    if (intent.layer === 'multi') {
      // Validate multi-layer query structure
      if (!intent.layers || !Array.isArray(intent.layers) || intent.layers.length === 0) {
        throw new Error('Invalid multi-layer query - missing layers array');
      }
      console.log('🌟 Multi-layer query detected:', intent.layers);
      return intent;
    }

    // Handle proximity queries
    if (intent.layer === 'proximity') {
      // Validate proximity query structure - accept both single and multi-center
      const hasCenterTract = intent.centerTract || (intent.centerTracts && intent.centerTracts.length > 0);
      if (!hasCenterTract || !intent.radius) {
        throw new Error('Invalid proximity query - missing centerTract/centerTracts or radius');
      }
      return intent;
    }

    // Handle contract-based queries
    if (intent.layer === 'contracts') {
      // Validate contract query structure
      if (!intent.contractFilters) {
        throw new Error('Invalid contract query - missing contractFilters');
      }
      return intent;
    }

    // Validate the response for spatial queries
    if (!intent.layer || !intent.conditions) {
      throw new Error('Invalid response from LLM');
    }

    // Normalize conditions for SQL generation
    intent.conditions = intent.conditions.map(cond => {
      // Handle IN operator - convert to array if not already
      if (cond.operator === 'IN' && !Array.isArray(cond.value)) {
        cond.value = [cond.value];
      }
      return cond;
    });

    // Add assistant response to conversation history
    conversationService.addMessage('assistant', intent.description);

    console.log('Query translated:', intent);

    return intent;
  } catch (error) {
    console.error('NLP translation failed:', error);

    // Try fallback pattern matching
    return fallbackPatternMatching(userQuery);
  }
}

/**
 * Fallback pattern matching if OpenAI fails
 * Uses simple regex patterns to extract query intent
 */
function fallbackPatternMatching(query) {
  const lowerQuery = query.toLowerCase();

  // Pattern: tract search
  const tractPattern = /(?:show|find).*tract[s]?\s+(?:in\s+)?([a-z]+)\s*county/i;
  const tractMatch = query.match(tractPattern);
  if (tractMatch) {
    return {
      layer: 'tracts',
      conditions: [{
        field: 'CountyName',
        operator: '=',
        value: tractMatch[1].toUpperCase(),
        type: 'string'
      }],
      zoom: true,
      description: `Showing tracts in ${tractMatch[1]} County`,
      confidence: 0.7,
      fallback: true
    };
  }

  // Pattern: operator filter for wells
  if (lowerQuery.includes('operated by') || lowerQuery.includes('operator')) {
    const operatorPattern = /operated?\s+by\s+([a-z0-9\s,&]+)/i;
    const operatorMatch = query.match(operatorPattern);

    if (operatorMatch) {
      const operatorName = operatorMatch[1].trim();
      return {
        layer: 'wells',
        conditions: [{
          field: 'ENVOperator',
          operator: 'LIKE',
          value: `%${operatorName}%`,
          type: 'string'
        }],
        zoom: false,
        description: `Filtering wells by operator: ${operatorName}`,
        confidence: 0.7,
        fallback: true
      };
    }
  }

  // Pattern: well status filter
  if (lowerQuery.includes('producing') || lowerQuery.includes('drilling') || lowerQuery.includes('duc')) {
    let status = 'PRODUCING';
    if (lowerQuery.includes('drilling')) status = 'DRILLING';
    if (lowerQuery.includes('duc') || lowerQuery.includes('uncompleted')) status = 'DUC';
    if (lowerQuery.includes('permitted')) status = 'PERMITTED';
    if (lowerQuery.includes('shut-in') || lowerQuery.includes('shut in')) status = 'SHUT-IN';

    return {
      layer: 'wells',
      conditions: [{
        field: 'ENVWellStatus',
        operator: '=',
        value: status,
        type: 'string'
      }],
      zoom: false,
      description: `Filtering to ${status} wells`,
      confidence: 0.7,
      fallback: true
    };
  }

  // Pattern: formation filter
  const formations = ['wolfcamp', 'bone spring', 'avalon', 'delaware', 'spraberry'];
  for (const formation of formations) {
    if (lowerQuery.includes(formation)) {
      return {
        layer: 'wells',
        conditions: [{
          field: 'ENVInterval',
          operator: 'LIKE',
          value: `%${formation.toUpperCase()}%`,
          type: 'string'
        }],
        zoom: false,
        description: `Showing ${formation} wells`,
        confidence: 0.7,
        fallback: true
      };
    }
  }

  // Pattern: county filter for wells
  const countyPattern = /(?:wells?|in)\s+([a-z]+)\s*county/i;
  const countyMatch = query.match(countyPattern);
  if (countyMatch) {
    return {
      layer: 'wells',
      conditions: [{
        field: 'County',
        operator: '=',
        value: countyMatch[1].toUpperCase(),
        type: 'string'
      }],
      zoom: true,
      description: `Showing wells in ${countyMatch[1]} County`,
      confidence: 0.7,
      fallback: true
    };
  }

  // Default: return error
  throw new Error('Could not understand query. Try: "Show wells operated by Diamondback" or "Find tracts in Howard County"');
}

/**
 * Suggest example queries to help users
 */
export function getExampleQueries() {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  // Nexus Emissions tenant: gas utility leak detection and fenceline
  if (hostname.includes('nexus-emissions') || hostname.includes('emissions')) {
    return [
      {
        category: 'Leak Detection',
        queries: [
          'Show all leaks',
          'Show Grade 1 leaks',
          'Show open leaks',
          'Show high methane leaks'
        ]
      },
      {
        category: 'Gas Infrastructure',
        queries: [
          'Show distribution mains',
          'Show transmission mains',
          'Show service lines',
          'Show high-risk pipes'
        ]
      },
      {
        category: 'Field Operations',
        queries: [
          'Show open work orders',
          'Show completed repairs',
          'Show survey routes',
          'Show field crews'
        ]
      },
      {
        category: 'Fenceline Monitoring',
        queries: [
          'Show fenceline sensors',
          'Show process units',
          'Show facility boundary',
          'Show dispersion plumes'
        ]
      }
    ];
  }

  // Select Water Solutions tenant
  if (hostname.includes('nexus-water') || hostname.includes('water')) {
    return [
      {
        category: '💧 Water Sources',
        queries: [
          'Show all water sources',
          'Find water sources in Reeves County',
          'Show high-volume water sources',
          'Find active sourcing operations'
        ]
      },
      {
        category: '🏭 Disposal Wells',
        queries: [
          'Show disposal wells in Midland',
          'Find high-utilization disposal wells',
          'Show disposal capacity by county',
          'Find disposal wells by operator'
        ]
      },
      {
        category: '🔧 Treatment Facilities',
        queries: [
          'Show all treatment facilities',
          'Find facilities with high recycling rates',
          'Show treatment capacity in Permian',
          'Find facilities using advanced technology'
        ]
      },
      {
        category: '🚿 Transfer Pipelines',
        queries: [
          'Show water transfer pipelines',
          'Find pipelines in Reeves County',
          'Show high-capacity pipeline routes',
          'Find active transfer operations'
        ]
      }
    ];
  }

  // Default TPL queries
  return [
    {
      category: 'Land & Royalty Interest',
      queries: [
        'Show tracts in Reeves County',
        'Find fee tracts in Howard County',
        'Show tracts with NRI over 0.01',
        'Find tracts with ORRI over 0.02'
      ]
    },
    {
      category: 'Acreage Analysis',
      queries: [
        'Show tracts over 200 acres',
        'Find tracts with NRA over 20',
        'Show tracts over 500 acres in Culberson',
        'Find tracts in Midland County'
      ]
    },
    {
      category: 'Deal & Asset Tracking',
      queries: [
        'Show SM ORRI Deal 1 tracts',
        'Find Tumbleweed Deal assets',
        'Show Allegro Deal tracts',
        'Find DE ORRI Deal 1 tracts'
      ]
    },
    {
      category: 'Geographic Focus',
      queries: [
        'Show all tracts in Culberson County',
        'Find tracts in Glasscock County',
        'Show tracts in Martin County',
        'Find tracts in Loving County'
      ]
    },
    {
      category: 'Survey & Block',
      queries: [
        'Find T&P RR CO survey tracts',
        'Show tracts in Section 10',
        'Find L&SV RR CO surveys',
        'Show tracts in Section 1'
      ]
    },
    {
      category: 'Advanced Queries',
      queries: [
        'Fee tracts over 300 acres in Reeves',
        'Tracts with NRI over 0.02 in Howard',
        'Large tracts in Culberson County',
        'Show Upton County tracts over 100 acres'
      ]
    },
    {
      category: '🌟 Multi-Layer (NEW!)',
      queries: [
        'Show all data in Reeves County',
        'Show tracts and wells in Howard County',
        'Display all layers for Culberson',
        'Show everything in Midland County'
      ]
    }
  ];
}

// Export conversation service for use in other components
export { conversationService };

export default {
  translateQuery,
  getExampleQueries
};
